/**
 * Three mutually exclusive D-Tools pricing adjustment methods.
 *
 * Percentage / fixed amount / final agreed price — one shared calculation authority
 * for React, server, PDF, and invoice paths. `agreedPriceSgd` remains the canonical
 * final commercial price consumed downstream.
 */
import type { RetailPricingJson } from "./proposalCustomerPricing";
import { parseProposalCurrencyAmount } from "./proposalCurrencyAmount";
import {
  canonicalExGstFromGstInclusiveNett,
  isGstInclusiveFinalAgreedPrice,
  resolveAgreedPriceBasis,
  type ProposalAgreedPriceBasis,
} from "./proposalAgreedPriceBasis";

export const PROPOSAL_DISCOUNT_METHODS = [
  "percentage",
  "fixed_amount",
  "final_agreed_price",
] as const;

export type ProposalDiscountMethod = (typeof PROPOSAL_DISCOUNT_METHODS)[number];

export type ProposalDiscountDraftInput = {
  method: ProposalDiscountMethod | "none" | null | undefined;
  /** Staff-entered percentage (0–100). */
  discountPercentage?: unknown;
  /** Staff-entered fixed discount SGD. */
  specialDiscountSgd?: unknown;
  /** Staff-entered final agreed price SGD. */
  agreedPriceSgd?: unknown;
  showDiscountPctToCustomer?: boolean | null;
};

export type NormalizedProposalDiscount = {
  discountMethod: ProposalDiscountMethod | null;
  discountPercentage: number | null;
  specialDiscountSgd: number | null;
  agreedPriceSgd: number | null;
  showDiscountPctToCustomer: boolean | null;
  /** True when a negotiated discount is commercially active. */
  hasDiscount: boolean;
  discountAmount: number;
  finalAgreedPrice: number;
  derivedPercentage: number;
};

export type ProposalDiscountValidationResult =
  | { ok: true; normalized: NormalizedProposalDiscount }
  | { ok: false; message: string };

function roundMoney(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

function isMalformedNumber(value: unknown): boolean {
  if (value == null || value === "") return false;
  if (typeof value === "number") return !Number.isFinite(value);
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return false;
    if (/[,\$]|S\$/i.test(trimmed)) {
      return !parseProposalCurrencyAmount(trimmed, { allowZero: true }).ok;
    }
    const n = Number(trimmed);
    return !Number.isFinite(n);
  }
  return true;
}

/** Parse a money/percentage draft string or number; blank → null (no discount). */
export function parseOptionalDiscountNumber(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  if (typeof value === "string" && /[,\$]|S\$/i.test(value)) {
    const parsed = parseProposalCurrencyAmount(value, { allowZero: true });
    return parsed.ok ? parsed.amount : Number.NaN;
  }
  if (isMalformedNumber(value)) return Number.NaN;
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return Number.NaN;
  return n;
}

export function isProposalDiscountMethod(value: unknown): value is ProposalDiscountMethod {
  return (
    value === "percentage" || value === "fixed_amount" || value === "final_agreed_price"
  );
}

/**
 * Infer method from legacy retailPricingJson when discountMethod is absent.
 * Does not mutate historical payloads — resolution only.
 */
export function inferLegacyProposalDiscountMethod(
  retail: RetailPricingJson | null | undefined,
): ProposalDiscountMethod | null {
  if (!retail) return null;
  if (isProposalDiscountMethod(retail.discountMethod)) return retail.discountMethod;
  const agreed = parseOptionalDiscountNumber(retail.agreedPriceSgd ?? retail.agreedPrice);
  if (agreed != null && Number.isFinite(agreed)) return "final_agreed_price";
  const fixed = parseOptionalDiscountNumber(retail.specialDiscountSgd);
  if (fixed != null && Number.isFinite(fixed) && fixed > 0.004) return "fixed_amount";
  return null;
}

function derivedPct(discountAmount: number, proposalTotal: number): number {
  if (proposalTotal <= 0.004) return 0;
  return roundMoney((discountAmount / proposalTotal) * 100);
}

/**
 * Compute commercial discount snapshot from an explicit method + staff-entered value.
 * Blank / missing entered value → no discount (final = proposal total).
 */
export function computeProposalDiscountFromMethod(input: {
  proposalTotal: number;
  method: ProposalDiscountMethod | null;
  discountPercentage?: unknown;
  specialDiscountSgd?: unknown;
  agreedPriceSgd?: unknown;
  showDiscountPctToCustomer?: boolean | null;
}): ProposalDiscountValidationResult {
  const proposalTotal = roundMoney(Math.max(0, Number(input.proposalTotal) || 0));
  const method = input.method;

  if (method == null) {
    return {
      ok: true,
      normalized: {
        discountMethod: null,
        discountPercentage: null,
        specialDiscountSgd: null,
        agreedPriceSgd: null,
        showDiscountPctToCustomer: input.showDiscountPctToCustomer ?? null,
        hasDiscount: false,
        discountAmount: 0,
        finalAgreedPrice: proposalTotal,
        derivedPercentage: 0,
      },
    };
  }

  if (method === "percentage") {
    const raw = parseOptionalDiscountNumber(input.discountPercentage);
    if (raw != null && Number.isNaN(raw)) {
      return { ok: false, message: "Percentage discount must be a valid number." };
    }
    if (raw == null) {
      return {
        ok: true,
        normalized: {
          discountMethod: "percentage",
          discountPercentage: null,
          specialDiscountSgd: null,
          agreedPriceSgd: null,
          showDiscountPctToCustomer: input.showDiscountPctToCustomer ?? true,
          hasDiscount: false,
          discountAmount: 0,
          finalAgreedPrice: proposalTotal,
          derivedPercentage: 0,
        },
      };
    }
    if (raw < 0 || raw > 100) {
      return { ok: false, message: "Percentage discount must be between 0 and 100." };
    }
    const discountPercentage = roundMoney(raw);
    if (discountPercentage <= 0.004) {
      return {
        ok: true,
        normalized: {
          discountMethod: "percentage",
          discountPercentage: 0,
          specialDiscountSgd: null,
          agreedPriceSgd: null,
          showDiscountPctToCustomer: input.showDiscountPctToCustomer ?? true,
          hasDiscount: false,
          discountAmount: 0,
          finalAgreedPrice: proposalTotal,
          derivedPercentage: 0,
        },
      };
    }
    const discountAmount = roundMoney((proposalTotal * discountPercentage) / 100);
    const finalAgreedPrice = roundMoney(proposalTotal - discountAmount);
    return {
      ok: true,
      normalized: {
        discountMethod: "percentage",
        discountPercentage,
        specialDiscountSgd: discountAmount,
        agreedPriceSgd: finalAgreedPrice,
        showDiscountPctToCustomer: input.showDiscountPctToCustomer ?? true,
        hasDiscount: discountAmount > 0.004,
        discountAmount,
        finalAgreedPrice,
        derivedPercentage: discountPercentage,
      },
    };
  }

  if (method === "fixed_amount") {
    const raw = parseOptionalDiscountNumber(input.specialDiscountSgd);
    if (raw != null && Number.isNaN(raw)) {
      return { ok: false, message: "Fixed discount must be a valid number." };
    }
    if (raw == null) {
      return {
        ok: true,
        normalized: {
          discountMethod: "fixed_amount",
          discountPercentage: null,
          specialDiscountSgd: null,
          agreedPriceSgd: null,
          showDiscountPctToCustomer: input.showDiscountPctToCustomer ?? null,
          hasDiscount: false,
          discountAmount: 0,
          finalAgreedPrice: proposalTotal,
          derivedPercentage: 0,
        },
      };
    }
    if (raw < 0) {
      return { ok: false, message: "Fixed discount cannot be negative." };
    }
    if (raw > proposalTotal + 0.004) {
      return { ok: false, message: "Fixed discount cannot exceed Proposal Total." };
    }
    const discountAmount = roundMoney(raw);
    if (discountAmount <= 0.004) {
      return {
        ok: true,
        normalized: {
          discountMethod: "fixed_amount",
          discountPercentage: null,
          specialDiscountSgd: 0,
          agreedPriceSgd: null,
          showDiscountPctToCustomer: input.showDiscountPctToCustomer ?? null,
          hasDiscount: false,
          discountAmount: 0,
          finalAgreedPrice: proposalTotal,
          derivedPercentage: 0,
        },
      };
    }
    const finalAgreedPrice = roundMoney(proposalTotal - discountAmount);
    const pct = derivedPct(discountAmount, proposalTotal);
    return {
      ok: true,
      normalized: {
        discountMethod: "fixed_amount",
        discountPercentage: pct,
        specialDiscountSgd: discountAmount,
        agreedPriceSgd: finalAgreedPrice,
        showDiscountPctToCustomer: input.showDiscountPctToCustomer ?? null,
        hasDiscount: true,
        discountAmount,
        finalAgreedPrice,
        derivedPercentage: pct,
      },
    };
  }

  // final_agreed_price
  const raw = parseOptionalDiscountNumber(input.agreedPriceSgd);
  if (raw != null && Number.isNaN(raw)) {
    return { ok: false, message: "Final agreed price must be a valid number." };
  }
  if (raw == null) {
    return {
      ok: true,
      normalized: {
        discountMethod: "final_agreed_price",
        discountPercentage: null,
        specialDiscountSgd: null,
        agreedPriceSgd: null,
        showDiscountPctToCustomer: input.showDiscountPctToCustomer ?? null,
        hasDiscount: false,
        discountAmount: 0,
        finalAgreedPrice: proposalTotal,
        derivedPercentage: 0,
      },
    };
  }
  if (raw <= 0) {
    return { ok: false, message: "Final agreed price must be a positive amount." };
  }
  if (raw > proposalTotal + 0.004) {
    return {
      ok: false,
      message: "Final agreed price cannot exceed Proposal Total for a discount workflow.",
    };
  }
  const finalAgreedPrice = roundMoney(raw);
  const discountAmount = roundMoney(Math.max(0, proposalTotal - finalAgreedPrice));
  if (discountAmount <= 0.004) {
    return {
      ok: true,
      normalized: {
        discountMethod: "final_agreed_price",
        discountPercentage: null,
        specialDiscountSgd: null,
        agreedPriceSgd: finalAgreedPrice,
        showDiscountPctToCustomer: input.showDiscountPctToCustomer ?? null,
        hasDiscount: false,
        discountAmount: 0,
        finalAgreedPrice,
        derivedPercentage: 0,
      },
    };
  }
  const pct = derivedPct(discountAmount, proposalTotal);
  return {
    ok: true,
    normalized: {
      discountMethod: "final_agreed_price",
      discountPercentage: pct,
      specialDiscountSgd: discountAmount,
      agreedPriceSgd: finalAgreedPrice,
      showDiscountPctToCustomer: input.showDiscountPctToCustomer ?? null,
      hasDiscount: true,
      discountAmount,
      finalAgreedPrice,
      derivedPercentage: pct,
    },
  };
}

/**
 * Fail closed when conflicting modes/values arrive together without a coherent method.
 */
export function detectConflictingDiscountPayload(retail: RetailPricingJson | null | undefined): string | null {
  if (!retail) return null;
  const method = isProposalDiscountMethod(retail.discountMethod) ? retail.discountMethod : null;
  const hasPct =
    retail.discountPercentage != null &&
    Number.isFinite(Number(retail.discountPercentage)) &&
    Number(retail.discountPercentage) > 0.004;
  const hasFixed =
    retail.specialDiscountSgd != null &&
    Number.isFinite(Number(retail.specialDiscountSgd)) &&
    Number(retail.specialDiscountSgd) > 0.004;
  const hasAgreed =
    (retail.agreedPriceSgd != null && Number.isFinite(Number(retail.agreedPriceSgd))) ||
    (retail.agreedPrice != null && Number.isFinite(Number(retail.agreedPrice)));

  if (method == null && hasPct && (hasFixed || hasAgreed)) {
    return "Conflicting discount fields without discountMethod — refuse to apply both percentage and amount discounts.";
  }
  if (method === "percentage" && hasAgreed && !hasPct) {
    // Allowed only as calculated snapshot; staff entry for percentage mode is percentage.
  }
  if (method === "fixed_amount" && hasPct && !hasFixed && hasAgreed) {
    // Derived percentage on snapshot is fine.
  }
  // Tamper: both specialDiscount and agreedPrice that disagree with method semantics.
  if (method === "percentage" && hasFixed && hasAgreed && hasPct) {
    const recomputed = computeProposalDiscountFromMethod({
      proposalTotal: Number(retail.listTotal ?? retail.originalListPrice ?? 0),
      method: "percentage",
      discountPercentage: retail.discountPercentage,
      showDiscountPctToCustomer: retail.showDiscountPctToCustomer,
    });
    if (recomputed.ok) {
      const expectedAgreed = recomputed.normalized.agreedPriceSgd;
      const expectedFixed = recomputed.normalized.specialDiscountSgd;
      if (
        expectedAgreed != null &&
        Math.abs(Number(retail.agreedPriceSgd) - expectedAgreed) > 0.02
      ) {
        return "Tampered percentage discount payload: agreedPriceSgd does not match discountPercentage.";
      }
      if (
        expectedFixed != null &&
        Math.abs(Number(retail.specialDiscountSgd) - expectedFixed) > 0.02
      ) {
        return "Tampered percentage discount payload: specialDiscountSgd does not match discountPercentage.";
      }
    }
  }
  return null;
}

/**
 * Normalise retail pricing discount fields against an authoritative list total.
 * Clears inactive method fields; legacy records without discountMethod keep meaning.
 */
export function normalizeRetailPricingDiscountFields(input: {
  proposalTotal: number;
  retail: RetailPricingJson | null | undefined;
  /** When true, reject conflicting/tampered combinations. */
  failClosedOnConflict?: boolean;
  invoiceGstEnabled?: boolean | null;
}): ProposalDiscountValidationResult & { retailPatch?: RetailPricingJson } {
  const retail = input.retail ?? {};
  if (input.failClosedOnConflict) {
    const conflict = detectConflictingDiscountPayload(retail);
    if (conflict) return { ok: false, message: conflict };
  }

  const method =
    isProposalDiscountMethod(retail.discountMethod)
      ? retail.discountMethod
      : inferLegacyProposalDiscountMethod(retail);

  const agreedPriceBasis = resolveAgreedPriceBasis(retail);
  const gstInclusive = isGstInclusiveFinalAgreedPrice({
    discountMethod: method,
    agreedPriceBasis,
    invoiceGstEnabled: input.invoiceGstEnabled === true,
  });

  const enteredAgreed = parseOptionalDiscountNumber(
    retail.enteredAgreedPriceSgd ?? retail.agreedPriceSgd ?? retail.agreedPrice,
  );
  const agreedForCalc = gstInclusive && enteredAgreed != null && Number.isFinite(enteredAgreed)
    ? canonicalExGstFromGstInclusiveNett(enteredAgreed)
    : (retail.agreedPriceSgd ?? retail.agreedPrice);

  const result = computeProposalDiscountFromMethod({
    proposalTotal: input.proposalTotal,
    method,
    discountPercentage: retail.discountPercentage,
    specialDiscountSgd: retail.specialDiscountSgd,
    agreedPriceSgd: agreedForCalc,
    showDiscountPctToCustomer: retail.showDiscountPctToCustomer,
  });
  if (!result.ok) return result;

  const n = result.normalized;
  const retailPatch: RetailPricingJson = {
    ...retail,
    discountMethod: n.discountMethod,
    discountPercentage: n.discountPercentage,
    specialDiscountSgd: n.specialDiscountSgd,
    agreedPriceSgd: n.agreedPriceSgd,
    showDiscountPctToCustomer: n.showDiscountPctToCustomer,
    agreedPriceBasis: method === "final_agreed_price" ? agreedPriceBasis : resolveAgreedPriceBasis(retail),
  };
  if (method === "final_agreed_price") {
    const enteredFigure =
      gstInclusive && enteredAgreed != null && Number.isFinite(enteredAgreed)
        ? roundMoney(enteredAgreed)
        : n.agreedPriceSgd;
    retailPatch.enteredAgreedPriceSgd = enteredFigure;
    retailPatch.agreedPriceBasis = agreedPriceBasis;
  }
  // Remove legacy alias when canonical is set.
  if (retailPatch.agreedPrice != null) {
    delete retailPatch.agreedPrice;
  }

  return { ok: true, normalized: n, retailPatch };
}

/** UI helper: clear inactive fields when staff switches method. */
export function switchProposalDiscountMethod(
  nextMethod: ProposalDiscountMethod | "none",
  previous: ProposalDiscountDraftInput,
): ProposalDiscountDraftInput {
  if (nextMethod === "none") {
    return {
      method: null,
      discountPercentage: "",
      specialDiscountSgd: "",
      agreedPriceSgd: "",
      showDiscountPctToCustomer: previous.showDiscountPctToCustomer ?? null,
    };
  }
  if (nextMethod === "percentage") {
    return {
      method: "percentage",
      discountPercentage: previous.method === "percentage" ? previous.discountPercentage ?? "" : "",
      specialDiscountSgd: "",
      agreedPriceSgd: "",
      showDiscountPctToCustomer: true,
    };
  }
  if (nextMethod === "fixed_amount") {
    return {
      method: "fixed_amount",
      discountPercentage: "",
      specialDiscountSgd:
        previous.method === "fixed_amount" ? previous.specialDiscountSgd ?? "" : "",
      agreedPriceSgd: "",
      showDiscountPctToCustomer: previous.showDiscountPctToCustomer ?? false,
    };
  }
  return {
    method: "final_agreed_price",
    discountPercentage: "",
    specialDiscountSgd: "",
    agreedPriceSgd:
      previous.method === "final_agreed_price" ? previous.agreedPriceSgd ?? "" : "",
    showDiscountPctToCustomer: previous.showDiscountPctToCustomer ?? false,
  };
}

/**
 * Inherit discount method/value onto a revised list total.
 * Percentage recalculates; fixed/final retain entered value and re-validate.
 */
export function inheritProposalDiscountForRevisedListTotal(input: {
  revisedListTotal: number;
  parentRetail: RetailPricingJson | null | undefined;
  invoiceGstEnabled?: boolean | null;
}): ProposalDiscountValidationResult & { retailPatch?: RetailPricingJson } {
  const parent = input.parentRetail ?? {};
  const method =
    isProposalDiscountMethod(parent.discountMethod)
      ? parent.discountMethod
      : inferLegacyProposalDiscountMethod(parent);

  if (method == null) {
    return normalizeRetailPricingDiscountFields({
      proposalTotal: input.revisedListTotal,
      retail: { ...parent, discountMethod: null, discountPercentage: null, specialDiscountSgd: null, agreedPriceSgd: null },
    });
  }

  if (method === "percentage") {
    return normalizeRetailPricingDiscountFields({
      proposalTotal: input.revisedListTotal,
      retail: {
        ...parent,
        discountMethod: "percentage",
        discountPercentage: parent.discountPercentage,
        specialDiscountSgd: null,
        agreedPriceSgd: null,
      },
      failClosedOnConflict: true,
      invoiceGstEnabled: input.invoiceGstEnabled,
    });
  }

  if (method === "fixed_amount") {
    return normalizeRetailPricingDiscountFields({
      proposalTotal: input.revisedListTotal,
      retail: {
        ...parent,
        discountMethod: "fixed_amount",
        discountPercentage: null,
        specialDiscountSgd: parent.specialDiscountSgd,
        agreedPriceSgd: null,
      },
      failClosedOnConflict: true,
      invoiceGstEnabled: input.invoiceGstEnabled,
    });
  }

  return normalizeRetailPricingDiscountFields({
    proposalTotal: input.revisedListTotal,
    retail: {
      ...parent,
      discountMethod: "final_agreed_price",
      discountPercentage: null,
      specialDiscountSgd: null,
      agreedPriceSgd: parent.agreedPriceSgd ?? parent.agreedPrice,
      enteredAgreedPriceSgd: parent.enteredAgreedPriceSgd ?? parent.agreedPriceSgd ?? parent.agreedPrice,
      agreedPriceBasis: resolveAgreedPriceBasis(parent),
    },
    failClosedOnConflict: true,
    invoiceGstEnabled: input.invoiceGstEnabled,
  });
}

/** Apply normalised discount fields onto a retail pricing object (preserves other keys). */
export function applyNormalizedDiscountToRetailPricing(
  base: RetailPricingJson,
  normalized: NormalizedProposalDiscount,
): RetailPricingJson {
  const next: RetailPricingJson = {
    ...base,
    discountMethod: normalized.discountMethod,
    discountPercentage: normalized.discountPercentage,
    specialDiscountSgd: normalized.specialDiscountSgd,
    agreedPriceSgd: normalized.agreedPriceSgd,
    showDiscountPctToCustomer: normalized.showDiscountPctToCustomer,
  };
  if ("agreedPrice" in next) delete next.agreedPrice;
  return next;
}

export function draftStringsFromRetailPricing(retail: RetailPricingJson | null | undefined): {
  method: ProposalDiscountMethod | "none";
  discountPercentage: string;
  specialDiscountSgd: string;
  agreedPriceSgd: string;
  agreedPriceBasis: ProposalAgreedPriceBasis;
  showDiscountPctToCustomer: boolean;
} {
  const method = inferLegacyProposalDiscountMethod(retail) ?? "none";
  const showPct =
    method === "percentage"
      ? retail?.showDiscountPctToCustomer !== false
      : retail?.showDiscountPctToCustomer === true;
  const entered =
    retail?.enteredAgreedPriceSgd != null && Number.isFinite(Number(retail.enteredAgreedPriceSgd))
      ? String(retail.enteredAgreedPriceSgd)
      : retail?.agreedPriceSgd != null && Number.isFinite(Number(retail.agreedPriceSgd))
        ? String(retail.agreedPriceSgd)
        : retail?.agreedPrice != null && Number.isFinite(Number(retail.agreedPrice))
          ? String(retail.agreedPrice)
          : "";
  return {
    method: method === null ? "none" : method,
    discountPercentage:
      retail?.discountPercentage != null && Number.isFinite(Number(retail.discountPercentage))
        ? String(retail.discountPercentage)
        : "",
    specialDiscountSgd:
      retail?.specialDiscountSgd != null && Number.isFinite(Number(retail.specialDiscountSgd))
        ? String(retail.specialDiscountSgd)
        : "",
    agreedPriceSgd: entered,
    agreedPriceBasis: resolveAgreedPriceBasis(retail),
    showDiscountPctToCustomer: showPct,
  };
}

export { roundMoney as roundProposalDiscountMoney };
